/**
 * @fileoverview Org portal — org-wide feature policy (Spec 11). Toggles the
 * default on/off state of each Hans feature for every instructor in the org.
 * Per-instructor overrides live on the instructor detail page.
 *
 * Substitute requests (docs/substitute-requests.md) are set here too, as the
 * organization default every program uses unless its own Settings tab says
 * otherwise.
 *
 * Both save each change at once, optimistically, one save at a time, and put
 * a refused change back to what the server last confirmed (useSaveQueue), so
 * one switch's answer never flips another back.
 */

import { useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { OrgAuthContext } from "@/App";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import { ORG_FEATURES } from "@/lib/orgFeatures";
import { Card, SectionHeader } from "@/components/studio/primitives";
import SubstitutePolicyFields from "@/components/org/SubstitutePolicyFields";
import useSaveQueue from "@/components/org/useSaveQueue";
import { DEFAULT_SUBSTITUTE_POLICY } from "@/lib/substitutes";

function Toggle({ on, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-pressed={on}
      className={`relative shrink-0 rounded-full transition-colors ${on ? "bg-[var(--accent-yellow)]" : "bg-[var(--studio-interactive)]"}`}
      style={{ width: 40, height: 22 }}
    >
      <span
        className="absolute top-0.5 rounded-full bg-[var(--studio-knob)] shadow-sm transition-transform"
        style={{ width: 18, height: 18, left: 2, transform: on ? "translateX(18px)" : "translateX(0)" }}
      />
    </button>
  );
}

/**
 * The organization's default substitute policy. Saved whole on every change,
 * optimistically, and rolled back if the save fails — a switch that claims to
 * have saved when it has not is worse than one that is slow. Being whole, an
 * older save landing after a newer one would undo it, hence one at a time.
 */
function SubstituteDefaults() {
  const [policy, setPolicy] = useState(null);
  const [failed, setFailed] = useState(false);
  const { run, confirm } = useSaveQueue(setPolicy);

  useEffect(() => {
    let live = true;
    orgApi
      .get("/org/substitute-policy")
      .then((res) => {
        if (!live) return;
        const loaded = res.data?.policy || res.data || DEFAULT_SUBSTITUTE_POLICY;
        confirm(loaded);
        setPolicy(loaded);
      })
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [confirm]);

  const save = async (next) => {
    setPolicy(next);
    try {
      await run(async () => {
        const res = await orgApi.put("/org/substitute-policy", next);
        return res.data?.policy || next;
      });
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't save"));
    }
  };

  return (
    <Card className="mt-6" padding={0} data-testid="org-substitute-defaults">
      <div className="px-5 pt-5 md:px-6">
        <SectionHeader className="mb-2">Substitute requests</SectionHeader>
        <p className="font-sans text-[13px] text-[var(--text-tertiary)]">
          The default for every class and camp program. A program can set its own on its Settings tab.
        </p>
      </div>
      {failed ? (
        <p className="px-5 py-5 font-sans text-[14px] text-[var(--text-tertiary)] md:px-6">
          Substitute requests couldn&rsquo;t be loaded.
        </p>
      ) : !policy ? (
        <p className="px-5 py-5 font-sans text-[14px] text-[var(--text-tertiary)] md:px-6">Loading…</p>
      ) : (
        <SubstitutePolicyFields value={policy} onChange={save} testId="org-sub-policy" />
      )}
    </Card>
  );
}

export default function OrgFeaturesPage() {
  const { features: ctxFeatures, refresh } = useContext(OrgAuthContext) || {};
  const [features, setFeatures] = useState(ctxFeatures || {});
  const { run, confirm, busy } = useSaveQueue(setFeatures);

  // The portal's copy (re-read after each save) is the truth; while a save is
  // in flight the switches keep showing it until its answer is in.
  useEffect(() => {
    if (!ctxFeatures) return;
    confirm(ctxFeatures);
    if (!busy()) setFeatures(ctxFeatures);
  }, [ctxFeatures, confirm, busy]);

  const toggle = async (key) => {
    const on = !features[key];
    setFeatures((f) => ({ ...f, [key]: on })); // optimistic
    try {
      await run(async (current) => {
        const res = await orgApi.put("/org/features", { features: { [key]: on } });
        return res.data?.features || { ...current, [key]: on };
      });
      refresh?.();
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't save"));
    }
  };

  return (
    <div>
      <h1 className="mb-6 font-serif text-2xl font-semibold text-[var(--text-primary)]">Features</h1>

      <Card>
        <SectionHeader>Organization default</SectionHeader>
        <p className="mb-5 font-sans text-[13px] text-[var(--text-tertiary)]">
          Turn Hans features on or off for every instructor in your organization. You can override any of these for an
          individual instructor from their page.
        </p>
        <div className="flex flex-col gap-4">
          {ORG_FEATURES.map(({ key, label, desc }) => (
            <div key={key} className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="font-sans text-[15px] text-[var(--text-primary)]">{label}</div>
                <div className="mt-0.5 font-sans text-[13px] text-[var(--text-tertiary)]">{desc}</div>
              </div>
              <Toggle on={!!features[key]} onClick={() => toggle(key)} testId={`org-feature-${key}`} />
            </div>
          ))}
        </div>
      </Card>

      <SubstituteDefaults />
    </div>
  );
}
